import { isWorkbookErrorCell, type WorkbookCell } from '@erp/shared-types';
import type { ColumnType } from '@erp/workbooks';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL = /^-?\d+(?:\.\d+)?$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z$/;

// A grid formats every cell: building an Intl formatter costs ~25x a format
// call, so each locale-and-options pair is built once.
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

/** A shared Intl.NumberFormat for this locale and these options. */
export function cellNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = JSON.stringify([locale, options]);
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, format);
  }
  return format;
}

function cellDateTimeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = JSON.stringify([locale, options]);
  let format = dateTimeFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, format);
  }
  return format;
}

/** A Number's text without insignificant zeros: 22.50 → 22.5, 10.000 → 10. */
function decimalText(text: string): string {
  if (!DECIMAL.test(text) || !text.includes('.')) return text;
  return text.replace(/\.?0+$/, '');
}

/**
 * A Number's text with its thousands grouped as the locale writes them, its
 * significant decimals kept: 14730 → 14,730, 2031.50 → 2,031.5. Anything but
 * a Number's text shows as it is.
 */
export function groupedDecimalText(text: string, locale: string): string {
  const trimmed = decimalText(text);
  if (!DECIMAL.test(trimmed)) return text;
  const decimals = trimmed.split('.')[1]?.length ?? 0;
  return cellNumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(trimmed));
}

/**
 * A non-empty cell in the one display format of its Declared type, the same in
 * every grid (Source previews, Worksheets, the Process tab, file previews):
 * TRUE/FALSE for a TRUE/FALSE column (the engine returns `true`/`false` text),
 * a Number without insignificant zeros, a Date column's ISO dates in the
 * locale's short date, a Date and time (an ISO instant) in the viewer's time
 * zone, naming the zone, and an Error cell as its error value (#N/A). Anything
 * else, including text that only looks like a date, a number or a boolean, and a
 * value that isn't its type, shows as it is.
 */
export function workbookCellText(
  cell: Exclude<WorkbookCell, null>,
  type: ColumnType | undefined,
  locale: string,
  /** The viewer's time zone unless given. */
  timeZone?: string,
): string {
  if (isWorkbookErrorCell(cell)) return cell.error;
  if (typeof cell === 'boolean') return cell ? 'TRUE' : 'FALSE';
  if (typeof cell !== 'string') return String(cell);
  switch (type) {
    case 'boolean':
      return cell === 'true' || cell === 'false' ? cell.toUpperCase() : cell;
    case 'decimal':
      return decimalText(cell);
    case 'date':
      return ISO_DATE.test(cell)
        ? // A date has no time zone: format it in UTC so it never shifts a day.
          formatInstant(`${cell}T00:00:00Z`, locale, { timeZone: 'UTC' }, cell)
        : cell;
    case 'datetime':
      return ISO_DATETIME.test(cell)
        ? formatInstant(
            cell,
            locale,
            {
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              timeZoneName: 'short',
              timeZone,
            },
            cell,
          )
        : cell;
    default:
      return cell;
  }
}

function formatInstant(
  iso: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return cellDateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    ...options,
  }).format(date);
}
